// The dice bag's pure half (slice 11; design/15-dice-bag.md, plan §2.6): what
// a die is, what a throw asks for, the structured record a throw leaves, the
// three-line format the log shows, and the bag a person carries from table
// to table. The bag throws dice, shows what came up and adds numbers — it
// knows no game's rules, so nothing here counts successes or judges a result.
//
// The record holds FACES, never integers: the coin's faces are "Heads" and
// "Tails" from the first version, and symbol dice later share the shape. The
// modifier applies ONCE to the whole pool: 3d20+2 rolling 9, 11, 2 is 24.
// No outcome is computed in a browser: `rollWith` takes the generator, and
// the server hands it `crypto.randomInt`; the browser never calls it.
import { dmAttackLines, parseAttackFacts } from '../rules/attack.js';
import { dmSaveLines, parseSaveFacts, saveDiceMatch } from '../rules/save.js';
import { diceText, isPlainDice } from '../rules/system.js';
import { addedModifier, parsePool, poolFaces, poolResult, poolText, throwPool } from './pool.js';
import { ADVANTAGE_LIMITS, advantageText, isAdvantageWord, parseAdvantageFacts } from '../rules/advantage.js';
import { parseTableFacts } from './tables.js';
import { parseTriggerFacts, triggerLines } from '../rules/triggers.js';
import { applyStops, parseDamageStops, stopLineText } from '../rules/damageStops.js';
import { parseTestFacts, testLines, testRollMatches } from '../rules/test.js';
export const DICE_LIMITS = { sides: 10_000, quantity: 1000, modifier: 100_000, label: 80 };
/** A quantity above this asks the person to confirm before throwing (design/15 §3). */
export const CONFIRM_ABOVE = 100;
export function parseDiceBoost(v) {
    if (typeof v !== 'object' || v === null || Array.isArray(v))
        return null;
    const b = v;
    if (Object.keys(b).some((k) => k !== 'maximum' && k !== 'share'))
        return null;
    if (b.maximum !== undefined && b.maximum !== true)
        return null;
    const s = b.share;
    if (s !== undefined && !(Array.isArray(s) && s.length === 2 && isInt(s[0], 1, 100) && isInt(s[1], 1, 100)))
        return null;
    if (b.maximum === undefined && s === undefined)
        return null;
    return { ...(b.maximum ? { maximum: true } : {}), ...(s ? { share: [s[0], s[1]] } : {}) };
}
/** A boosted total: `rolled` the dice as thrown, `most` their top faces, `bonus` what the throw adds. */
export function boostedTotal(rolled, most, bonus, b) {
    return (b.maximum ? most : rolled) + bonus + (b.share ? Math.floor(((rolled + bonus) * b.share[0]) / b.share[1]) : 0);
}
/** How many tokens one Apply may name, and the longest damage type (qa-runs#406). */
export const APPLY_LIMITS = { targets: 12, type: 24 };
/** Whether a roll in the log can be applied (qa-runs#406): a plain throw with a positive total, not an attack, a save, a table, a trigger or a test. */
export function applicable(r) {
    return !r.attack && !r.save && !r.table && !r.trigger && !r.test && r.die.kind !== 'coin' && (r.fudged?.total ?? r.total) >= 1;
}
/** What one application deals after what stops it. */
export function appliedDealt(a) {
    return a.stops && !a.heal ? applyStops(a.stops.rules, a.stops.creature, [{ types: a.types ?? [], amount: a.amount }], a.stops.overcome) : { total: a.amount, lines: [] };
}
/** One application as a player reads it: the roll's total, unless the DM told them what stops this creature. A Change of the DM's stays theirs to read. */
export function playerApplied(a) {
    if (!a.stops)
        return a;
    const { stops, ...seen } = a;
    const dealt = appliedDealt(a);
    if (stops.told)
        return { ...seen, stopped: dealt.lines.map(stopLineText) };
    return a.settled?.amount === dealt.total ? { ...seen, settled: { ...a.settled, amount: a.amount } } : seen;
}
/** The log's words for one application (qa-runs#406): *Mary applied 6 damage to Goblin 2, from her 1d6 + 2.* */
export function appliedLine(r, a, nameOf = (x) => (x.handle ? `@${x.handle}` : 'a player')) {
    const by = nameOf(a.by);
    const whose = a.by.id === r.actor.id ? (by === 'You' ? 'your' : 'their') : `${nameOf(r.actor)}'s`;
    const what = a.heal ? 'healing' : `${a.nonlethal ? 'nonlethal ' : ''}${a.type ? `${a.type} ` : ''}damage`;
    const dealt = appliedDealt(a);
    const lines = a.stops ? dealt.lines.map(stopLineText) : a.stopped ?? [];
    const head = `${by} applied ${a.amount} ${what} to ${a.name}, from ${whose} ${r.notation}${lines.length ? ` (${lines.join('; ')})` : ''}`;
    if (!a.settled)
        return `${head}: waiting for the DM.`;
    if (a.settled.amount === null)
        return `${head}: the DM dropped it.`;
    if (a.stopped?.length)
        return `${head}: ${a.settled.amount}.`;
    if (a.settled.amount !== dealt.total)
        return `${head}: the DM made it ${a.settled.amount}.`;
    return `${head}.`;
}
function parseApplied(v) {
    if (!Array.isArray(v) || v.length > 200)
        return null;
    const out = [];
    for (const x of v) {
        const a = x;
        if (!a || typeof a !== 'object' || !isUuid(a.target) || typeof a.name !== 'string' || a.name.length > 200 || !isUuid(a.scene) || typeof a.heal !== 'boolean')
            return null;
        if (!isInt(a.amount, 1, 100_000))
            return null;
        if (a.type !== undefined && (typeof a.type !== 'string' || a.type.length < 1 || a.type.length > APPLY_LIMITS.type))
            return null;
        if (a.nonlethal !== undefined && a.nonlethal !== true)
            return null;
        if (a.hidden !== undefined && a.hidden !== true)
            return null;
        if (a.types !== undefined && !(Array.isArray(a.types) && a.types.length <= 16 && a.types.every((t) => typeof t === 'string' && t.length >= 1 && t.length <= 40)))
            return null;
        if (a.stopped !== undefined && !(Array.isArray(a.stopped) && a.stopped.length <= 16 && a.stopped.every((t) => typeof t === 'string' && t.length <= 200)))
            return null;
        const stops = a.stops === undefined ? undefined : parseDamageStops(a.stops);
        if (stops === null)
            return null;
        const by = parseActor(a.by);
        if (!by)
            return null;
        let settled = null;
        if (a.settled !== null) {
            const st = a.settled;
            if (!st || typeof st !== 'object' || typeof st.auto !== 'boolean' || (st.amount !== null && !isInt(st.amount, 0, 100_000)))
                return null;
            settled = { amount: st.amount, auto: st.auto };
        }
        out.push({ target: a.target, name: a.name, scene: a.scene, heal: a.heal, amount: a.amount, ...(a.type !== undefined ? { type: a.type } : {}), ...(a.types ? { types: [...a.types] } : {}), ...(stops ? { stops } : {}), ...(a.stopped ? { stopped: [...a.stopped] } : {}), ...(a.nonlethal ? { nonlethal: true } : {}), by, ...(a.hidden ? { hidden: true } : {}), settled });
    }
    return out;
}
/**
 * The die the DM's fudge band rides on (issue 1038, Tom's choice A): the dice bag's d20, the physical die — not any game
 * system's roll. A table whose system throws no d20 never meets it.
 */
export const BAND_SIDES = 20;
/** A band from the wire or the stored bag: exact shape or null. */
export function parseBand(v) {
    if (typeof v !== 'object' || v === null)
        return null;
    const b = v;
    return isInt(b.min, 1, BAND_SIDES) && isInt(b.max, 1, BAND_SIDES) && b.min <= b.max ? { min: b.min, max: b.max } : null;
}
/** Whether a band may ride on this request: the DM's private d20 throw (Roll for included), and nothing else. */
export function bandApplies(req, role) {
    return role === 'dm' && req.die.kind === 'd' && req.die.sides === BAND_SIDES && (req.private || req.forUserId !== null);
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isInt = (v, min, max) => typeof v === 'number' && Number.isInteger(v) && v >= min && v <= max;
export function parseDie(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const d = value;
    if (d.kind === 'coin')
        return { kind: 'coin' };
    if (d.kind === 'd' && isInt(d.sides, 2, DICE_LIMITS.sides))
        return { kind: 'd', sides: d.sides };
    return null;
}
/** A request from the wire: exact shape or null. Untrusted input, always. */
export function parseRollRequest(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const b = value;
    if (b.kind !== 'roll.request')
        return null;
    const die = parseDie(b.die);
    if (!die)
        return null;
    const quantity = b.quantity === undefined ? 1 : b.quantity;
    if (!isInt(quantity, 1, DICE_LIMITS.quantity))
        return null;
    const modifier = b.modifier === undefined ? 0 : b.modifier;
    if (!isInt(modifier, -DICE_LIMITS.modifier, DICE_LIMITS.modifier))
        return null;
    if (die.kind === 'coin' && modifier !== 0)
        return null; // a coin's faces are not numbers (design/15 §2)
    const label = b.label === undefined ? '' : b.label;
    if (typeof label !== 'string' || label.length > DICE_LIMITS.label)
        return null;
    const priv = b.private === undefined ? false : b.private;
    if (typeof priv !== 'boolean')
        return null;
    const forUserId = b.forUserId === undefined || b.forUserId === null ? null : b.forUserId;
    if (forUserId !== null && !isUuid(forUserId))
        return null;
    const ask = b.ask === undefined || b.ask === null ? null : b.ask;
    if (ask !== null && (!isUuid(ask) || forUserId !== null))
        return null; // an answer is the player's own, never rolled for anyone
    const band = b.band === undefined || b.band === null ? null : parseBand(b.band);
    if (b.band !== undefined && b.band !== null && !band)
        return null;
    if (band && (ask !== null || die.kind !== 'd' || die.sides !== BAND_SIDES))
        return null; // a band is for its die, never an answer
    const token = b.token === undefined || b.token === null ? null : b.token;
    if (token !== null && !isUuid(token))
        return null;
    const boost = b.boost === undefined ? null : parseDiceBoost(b.boost);
    if (b.boost !== undefined && (!boost || die.kind !== 'd' || ask !== null || band))
        return null;
    // A word for the system's one die: never on an answer, whose throw is the ask's, nor with a band or a changed spell's dice.
    const check = b.check;
    if (b.check !== undefined && (typeof check !== 'object' || check === null || !['save', 'ability_check', 'skill'].includes(check.on) || (check.which !== undefined && !(typeof check.which === 'string' && check.which.length <= 80)) || !isUuid(check.token)))
        return null;
    if (b.advantage !== undefined && (!isAdvantageWord(b.advantage) || die.kind !== 'd' || quantity !== 1 || ask !== null || band || boost))
        return null;
    return { kind: 'roll.request', die, quantity, modifier, label: label.trim(), private: priv, forUserId, ...(ask ? { ask } : {}), ...(band ? { band } : {}), ...(token ? { token } : {}), ...(check ? { check: { on: check.on, token: check.token, ...(typeof check.which === 'string' ? { which: check.which } : {}) } } : {}), ...(boost ? { boost } : {}), ...(isAdvantageWord(b.advantage) ? { advantage: b.advantage } : {}) };
}
/** `3d20+2`, `1d20`, `d100-1`, `3 coins`, `coin`. */
export function notationOf(die, quantity, modifier) {
    if (die.kind === 'coin')
        return quantity === 1 ? 'coin' : `${quantity} coins`;
    const mod = modifier === 0 ? '' : modifier > 0 ? `+${modifier}` : `${modifier}`;
    return `${quantity}d${die.sides}${mod}`;
}
/**
 * Throw. Pure given the generator: the same faces make the same record, which
 * is what the tests hold to — and the tally, the collapse and the modifier
 * rule are all downstream of this one shape.
 */
export function rollWith(rng, req, actor, id, at, forWhom = null) {
    const dice = [];
    // A banded d20 (issue 1038) is thrown inside the band, every face in it equally likely: the generator still decides.
    const band = req.band && req.die.kind === 'd' && req.die.sides === BAND_SIDES ? req.band : null;
    for (let i = 0; i < req.quantity; i += 1) {
        if (req.die.kind === 'coin')
            dice.push(rng(2) === 1 ? 'Heads' : 'Tails');
        else if (band)
            dice.push(band.min + rng(band.max - band.min + 1) - 1);
        else
            dice.push(rng(req.die.sides));
    }
    const sum = dice.reduce((acc, f) => acc + (typeof f === 'number' ? f : 0), 0);
    const total = req.die.kind === 'coin' ? dice.filter((f) => f === 'Heads').length
        : req.boost ? boostedTotal(sum, req.quantity * req.die.sides, req.modifier, req.boost) : sum + req.modifier;
    return {
        id, notation: notationOf(req.die, req.quantity, req.modifier), die: req.die, dice, modifier: req.modifier, total,
        actor: forWhom ?? actor, by: forWhom ? actor : null, label: req.label, private: req.private, at, fudged: null,
        ...(req.ask ? { ask: req.ask } : {}),
        ...(band ? { band: { ...band } } : {}),
        ...(req.boost ? { boost: req.boost } : {}),
    };
}
/** A plain roll with advantage (docs/MRCS/dnd-5e.md §3.2.1): `dice` the throw made concrete, so a pool that keeps one. */
export function rollKept(rng, req, dice, facts, actor, id, at, forWhom = null) {
    const pool = throwPool(dice, rng);
    const { advantage: _word, check: _check, ...plain } = req;
    const mod = req.modifier === 0 ? '' : ` ${req.modifier > 0 ? '+' : '−'} ${Math.abs(req.modifier)}`;
    return { ...rollWith(() => 1, plain, actor, id, at, forWhom), notation: `${diceText(dice)}${mod}`, dice: poolFaces(pool), total: poolResult(dice, pool) + req.modifier, pool, advantage: facts };
}
/**
 * A logged roll fired again (issue 1037, DICE-007): a NEW request with the same die, quantity, modifier, label, privacy
 * and person rolled for, thrown by the server like any other — never a result computed here. Null for a roll this
 * viewer may not fire again: a player re-rolls only their own; the DM their own and the ones they made for a player.
 */
export function rerollOf(r, meId, isDm) {
    if (r.save)
        return null; // a resolve is the spell's, made once (issue 1129)
    if (r.test)
        return null; // a test is asked again from the Test control (issue 1523), never as its bare dice
    if (r.table)
        return null; // a table is rolled again from the Tables panel (issue 1158), never as its bare die
    if (r.ask)
        return null; // an answer to the DM's request is made once (issue 1049); its privacy is the ask's, not a request of one's own
    const own = r.by === null && r.actor.id === meId;
    const forAPlayer = isDm && r.by !== null && r.by.id === meId;
    if (!own && !forAPlayer)
        return null;
    return {
        kind: 'roll.request', die: r.die.kind === 'coin' ? { kind: 'coin' } : { kind: 'd', sides: r.die.sides }, quantity: r.dice.length,
        modifier: r.modifier, label: r.label, private: r.private, forUserId: forAPlayer ? r.actor.id : null,
        ...(r.band ? { band: { ...r.band } } : {}),
        ...(r.boost ? { boost: r.boost } : {}),
        ...(r.advantage?.result ? { quantity: 1, advantage: r.advantage.result } : {}),
    };
}
/** A tally of a coin throw: "2 heads, 1 tails". */
export function tally(dice) {
    const heads = dice.filter((f) => f === 'Heads').length;
    const tails = dice.length - heads;
    return `${heads} heads, ${tails} tails`;
}
/**
 * The log's format (design/15 §4): name and label, notation, math — three
 * lines; a single die with no modifier collapses to two; coins tally. The
 * DM's amendment shows beside the original, since the record keeps both.
 */
export function formatRoll(r, nameOf = (a) => (a.handle ? `@${a.handle}` : 'a player')) {
    const who = r.label ? `${nameOf(r.actor)} — ${r.label}:` : `${nameOf(r.actor)}:`;
    // A table's roll (issue 1158) reads "<table>: <outcome>", the die that picked it beside.
    if (r.table)
        return `${nameOf(r.actor)} — ${r.table.name}: ${r.table.text}\n${r.notation} = ${r.total}`;
    const shown = r.fudged ? `${r.fudged.total} (rolled ${r.total})` : String(r.total);
    if (r.die.kind === 'coin') {
        if (r.dice.length === 1)
            return `${who}\ncoin = ${r.dice[0]}`;
        return `${who}\n${r.notation}\n${tally(r.dice)}`;
    }
    if (r.dice.length === 1 && r.modifier === 0)
        return `${who}\n${r.notation} = ${shown}`;
    const mod = r.modifier === 0 ? '' : r.modifier > 0 ? `+${r.modifier}` : `${r.modifier}`;
    if (r.advantage?.result && r.pool && !r.attack)
        return `${who}\n${r.notation}\n${poolText(r.pool)}${mod} = ${shown}\n${advantageText(r.advantage)}`;
    return `${who}\n${r.notation}\n${r.dice.join(',')}${mod} = ${shown}`;
}
/** The whole log as the DM saves it: every entry in the log's own format, a blank line between (decisions 2, 20, 30). */
export function formatLog(records, nameOf) {
    // A trigger's chain (issue 1500) has no throw of its own: its lines are the record.
    return records.map((r) => `${r.trigger ? triggerLines(r.trigger).join('\n') : formatRoll(r, nameOf)}${r.attack ? `\n${dmAttackLines(r).join('\n')}` : ''}${r.save ? `\n${dmSaveLines(r).join('\n')}` : ''}${r.test ? `\n${testLines(r.test).join('\n')}` : ''}${r.private ? '\n(private)' : ''}${r.band ? `\n(banded ${r.band.min}–${r.band.max})` : ''}${r.by ? `\n(rolled by ${nameOf ? nameOf(r.by) : r.by.handle ? `@${r.by.handle}` : 'the DM'})` : ''}`).join('\n\n') + (records.length ? '\n' : '');
}
const STANDARD_SIDES = [4, 6, 8, 10, 12, 20, 100];
/** The base bag: d4 to d100, a coin, and the scratch `d?` (design/15 §8). */
export function defaultBag() {
    const rows = STANDARD_SIDES.map((sides) => ({ id: `d${sides}`, die: { kind: 'd', sides }, name: null, custom: false, quantity: 1, modifier: 0 }));
    rows.push({ id: 'coin', die: { kind: 'coin' }, name: null, custom: false, quantity: 1, modifier: 0 });
    rows.push({ id: 'd?', die: { kind: 'd', sides: 3 }, name: null, custom: true, quantity: 1, modifier: 0 });
    return { rows };
}
/** The standard rows, for Add Dice to restore a removed one (design/15 §2). */
export const standardRows = () => defaultBag().rows;
export const BAG_LIMITS = { rows: 60, name: 40, parts: 6 };
/** A stored or sent bag: exact shape or null. */
export function parseBag(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const rows = value.rows;
    if (!Array.isArray(rows) || rows.length > BAG_LIMITS.rows)
        return null;
    const out = [];
    const ids = new Set();
    for (const r of rows) {
        if (typeof r !== 'object' || r === null)
            return null;
        if (typeof r.id !== 'string' || r.id.length < 1 || r.id.length > 40 || ids.has(r.id))
            return null;
        const die = parseDie(r.die);
        if (!die)
            return null;
        const name = r.name === undefined || r.name === null ? null : r.name;
        if (name !== null && (typeof name !== 'string' || name.length < 1 || name.length > BAG_LIMITS.name))
            return null;
        if (!isInt(r.quantity ?? 1, 1, DICE_LIMITS.quantity) || !isInt(r.modifier ?? 0, -DICE_LIMITS.modifier, DICE_LIMITS.modifier))
            return null;
        const label = r.label === undefined || r.label === null ? null : r.label;
        if (label !== null && (typeof label !== 'string' || label.trim().length < 1 || label.length > DICE_LIMITS.label))
            return null;
        if (die.kind === 'coin' && label !== null && (r.modifier ?? 0) !== 0)
            return null;
        // A several-roll button (issue 1295): only on a saved button, one to six parts, each read like a row.
        let parts = null;
        if (r.parts !== undefined && r.parts !== null) {
            if (label === null || !Array.isArray(r.parts) || r.parts.length < 1 || r.parts.length > BAG_LIMITS.parts)
                return null;
            parts = [];
            for (const p of r.parts) {
                if (typeof p !== 'object' || p === null)
                    return null;
                const pd = parseDie(p.die);
                if (!pd || !isInt(p.quantity ?? 1, 1, DICE_LIMITS.quantity) || !isInt(p.modifier ?? 0, -DICE_LIMITS.modifier, DICE_LIMITS.modifier))
                    return null;
                if (pd.kind === 'coin' && (p.modifier ?? 0) !== 0)
                    return null;
                const pl = p.label === undefined || p.label === null || p.label === '' ? null : p.label;
                if (pl !== null && (typeof pl !== 'string' || pl.trim().length < 1 || pl.length > DICE_LIMITS.label))
                    return null;
                parts.push({ die: pd, quantity: (p.quantity ?? 1), modifier: (p.modifier ?? 0), ...(pl !== null ? { label: pl.trim() } : {}) });
            }
        }
        ids.add(r.id);
        out.push({ id: r.id, die, name, custom: r.custom === true, quantity: (r.quantity ?? 1), modifier: (r.modifier ?? 0), ...(label !== null ? { label: label.trim() } : {}), ...(parts ? { parts } : {}) });
    }
    const band = value.band;
    if (band !== undefined && band !== null && !parseBand(band))
        return null;
    return { rows: out, ...(band ? { band: parseBand(band) } : {}) };
}
/** Reset my dice (design/15 §6): own quantities to 1, own modifiers to 0; the rows and the log untouched. */
/** A saved roll button's throw (issue 1049): the row's die, quantity and modifier, under its own label. */
export function savedRollRequest(row, priv, forUserId, band = null) {
    if (!row.label)
        return null;
    const req = { kind: 'roll.request', die: row.die, quantity: row.quantity, modifier: row.die.kind === 'coin' ? 0 : row.modifier, label: row.label, private: priv, forUserId };
    return band && bandApplies(req, 'dm') ? { ...req, band } : req;
}
/**
 * A saved roll button's throws (issue 1295), in the button's order: a one-roll button is its one throw under its title
 * (exactly `savedRollRequest`); a several-roll button is one ordinary throw per part, each labelled
 * "<title>: <part label or its dice>". The bag sends each after the previous one's ack, so the log keeps this order.
 */
export function savedRollRequests(row, priv, forUserId, band = null) {
    if (!row.label)
        return [];
    if (!row.parts) {
        const one = savedRollRequest(row, priv, forUserId, band);
        return one ? [one] : [];
    }
    return row.parts.map((p) => {
        const modifier = p.die.kind === 'coin' ? 0 : p.modifier;
        const label = `${row.label}: ${p.label ?? notationOf(p.die, p.quantity, modifier)}`.slice(0, DICE_LIMITS.label);
        const req = { kind: 'roll.request', die: p.die, quantity: p.quantity, modifier, label, private: priv, forUserId };
        return band && bandApplies(req, 'dm') ? { ...req, band } : req;
    });
}
export function resetBag(bag) {
    // A saved roll button keeps what it was saved with (issue 1049): resetting the scratch rows does not unsave a roll.
    // The DM's fudge band is a setting, not a die (issue 1223): resetting the dice keeps it.
    return { rows: bag.rows.map((r) => (r.label ? r : { ...r, quantity: 1, modifier: 0 })), ...(bag.band ? { band: { ...bag.band } } : {}) };
}
/** A stored roll record from the log: exact shape or null (every stored body is read through a parser on replay). */
export function parseRollRecord(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const r = value;
    // A table of one outcome (issue 1158) throws a die of one side: its record alone may carry it.
    const oneSided = r.table !== undefined && typeof r.die === 'object' && r.die !== null && r.die.kind === 'd' && r.die.sides === 1 && Object.keys(r.die).length === 2;
    const die = oneSided ? { kind: 'd', sides: 1 } : parseDie(r.die);
    if (!isUuid(r.id) || !die || typeof r.notation !== 'string')
        return null;
    // A pool a modifier emptied threw no dice at all (issue 1312); every other throw threw one at least.
    if (!Array.isArray(r.dice) || (r.dice.length < 1 && !Array.isArray(r.pool) && r.trigger === undefined && r.save === undefined) || r.dice.length > DICE_LIMITS.quantity || !r.dice.every((f) => typeof f === 'number' || typeof f === 'string'))
        return null;
    if (!isInt(r.modifier, -DICE_LIMITS.modifier, DICE_LIMITS.modifier) || typeof r.total !== 'number' || !Number.isFinite(r.total))
        return null;
    const actor = parseActor(r.actor);
    if (!actor || typeof r.label !== 'string' || typeof r.private !== 'boolean' || typeof r.at !== 'string')
        return null;
    const by = r.by === undefined || r.by === null ? null : parseActor(r.by);
    if (r.by !== undefined && r.by !== null && !by)
        return null;
    let fudged = null;
    if (r.fudged !== undefined && r.fudged !== null) {
        const f = r.fudged;
        if (typeof f.total !== 'number' || !Number.isFinite(f.total) || typeof f.at !== 'string')
            return null;
        fudged = { total: f.total, at: f.at };
    }
    if (r.ask !== undefined && !isUuid(r.ask))
        return null;
    const attack = r.attack === undefined ? null : parseAttackFacts(r.attack);
    if (r.attack !== undefined && !attack)
        return null;
    const save = r.save === undefined ? null : parseSaveFacts(r.save);
    if (r.save !== undefined && (!save || attack || !saveDiceMatch(r.dice, save)))
        return null;
    const advantage = r.advantage === undefined ? null : parseAdvantageFacts(r.advantage);
    if (r.advantage !== undefined && (!advantage || save || r.table !== undefined || r.trigger !== undefined || r.test !== undefined))
        return null;
    if (advantage?.result && attack && !attack.rules.dice.keep)
        return null;
    // A pool rides only with an attack whose system's dice are more than a sum (issue 1312), and must be what they make:
    // its faces the roll's dice, its result the roll's total. A plain roll's rides only with its advantage, one die kept.
    let pool = null;
    if (advantage?.result && !attack) {
        const count = Array.isArray(r.pool) ? r.pool.length : 0;
        if (die.kind !== 'd' || count < 2 || count > 1 + ADVANTAGE_LIMITS.extraDice)
            return null;
        const kept = { count, sides: die.sides, keep: { which: advantage.result === 'advantage' ? 'highest' : 'lowest', n: 1 } };
        pool = parsePool(r.pool, kept, r.modifier);
        if (!pool)
            return null;
        const faces = poolFaces(pool);
        if (faces.length !== r.dice.length || faces.some((f, i) => f !== r.dice[i]) || r.total !== poolResult(kept, pool) + r.modifier)
            return null;
    }
    else if (attack && !isPlainDice(attack.rules.dice)) {
        pool = parsePool(r.pool, attack.rules.dice, r.modifier);
        if (!pool || die.kind !== 'd' || die.sides !== attack.rules.dice.sides)
            return null;
        const faces = poolFaces(pool);
        if (faces.length !== r.dice.length || faces.some((f, i) => f !== r.dice[i]))
            return null;
        if (r.total !== poolResult(attack.rules.dice, pool) + addedModifier(attack.rules.dice, r.modifier))
            return null;
    }
    else if (r.pool !== undefined)
        return null;
    // The band a roll was thrown in (issue 1222) is kept on replay, so the DM's log and saved file still say so after a reload.
    const band = r.band === undefined ? null : parseBand(r.band);
    if (r.band !== undefined && !band)
        return null;
    // A table's roll (issue 1158) is one die, no modifier, nothing else stamped on it.
    const table = r.table === undefined ? null : parseTableFacts(r.table);
    if (r.table !== undefined && (!table || attack || save || r.dice.length !== 1 || r.modifier !== 0 || die.kind !== 'd'))
        return null;
    // A trigger's record (issue 1500) is nothing else: no dice of its own, no attack, save or table beside it.
    const trigger = r.trigger === undefined ? null : parseTriggerFacts(r.trigger);
    if (r.trigger !== undefined && (!trigger || attack || save || table || r.dice.length !== 0))
        return null;
    // A test's record (issue 1523) is nothing else either: its dice are its sides' faces and its total the asking side's.
    const test = r.test === undefined ? null : parseTestFacts(r.test);
    if (r.test !== undefined && (!test || attack || save || table || trigger || (r.fudged !== undefined && r.fudged !== null)
        || die.kind !== 'd' || die.sides !== test.rules.dice.sides || !testRollMatches(r.dice, r.total, test)))
        return null;
    // Its applications (qa-runs#406) ride only on a plain throw.
    const applied = r.applied === undefined ? null : parseApplied(r.applied);
    if (r.applied !== undefined && (!applied || attack || save || table || trigger || test))
        return null;
    return { id: r.id, notation: r.notation, die, dice: [...r.dice], modifier: r.modifier, total: r.total, actor, by, label: r.label, private: r.private, at: r.at, fudged, ...(advantage ? { advantage } : {}), ...(r.ask ? { ask: r.ask } : {}), ...(band ? { band } : {}), ...(attack ? { attack } : {}), ...(pool ? { pool } : {}), ...(save ? { save } : {}), ...(table ? { table } : {}), ...(trigger ? { trigger } : {}), ...(test ? { test } : {}), ...(applied ? { applied } : {}) };
}
function parseActor(value) {
    if (typeof value !== 'object' || value === null)
        return null;
    const a = value;
    if (!isUuid(a.id))
        return null;
    if (a.handle !== null && a.handle !== undefined && typeof a.handle !== 'string')
        return null;
    if (a.character !== undefined && a.character !== null && !isUuid(a.character))
        return null;
    return { id: a.id, handle: typeof a.handle === 'string' ? a.handle : null, ...(a.character !== undefined ? { character: a.character } : {}) };
}
//# sourceMappingURL=dice.js.map